import { Link } from "@/lib/router-compat";
import { Calendar, Clock, ArrowRight, Newspaper, MapPin } from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { NewsPost } from "@/hooks/useNewsPosts";

interface NewsPostCardProps {
  post: NewsPost;
  blogPrefix: string;
  featured?: boolean;
}

// Map category to display name
const categoryLabels: Record<string, string> = {
  "Înmatriculări și parc auto": "Piață Auto",
  "RCA și asigurări": "Asigurări",
  "Reglementări": "Legislație",
  "Subvenții și programe": "Programe",
  "Import și export": "Import/Export",
  "Piață second-hand": "Second-Hand",
  "Electrice și hibride": "Electrice",
  "Dealer playbook": "Pentru Dealeri",
};

// Map tags to colors
const tagColors: Record<string, string> = {
  inmatriculari: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  parc_auto: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  masini_noi: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  second_hand: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
  electrice: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200",
  hibride: "bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200",
  rca: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  asigurari: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  rabla: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
  dealer_playbook: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
};

export const NewsPostCard = ({ post, blogPrefix, featured = false }: NewsPostCardProps) => {
  const categoryLabel = post.category ? (categoryLabels[post.category] || post.category) : "Știri";
  const displayTags = post.tags_structured?.topic_tags_display?.slice(0, 4) || [];
  const geoInfo = post.tags_structured?.geo;
  const hasGeo = geoInfo && (geoInfo.cities?.length || geoInfo.counties?.length);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("ro-RO", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  if (featured) {
    return (
      <Card className="overflow-hidden hover:shadow-lg transition-all border-primary/20 bg-gradient-to-br from-primary/5 to-transparent group">
        <CardHeader>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Badge variant="secondary" className="flex items-center gap-1">
              <Newspaper size={12} />
              {categoryLabel}
            </Badge>
            <Badge variant="outline" className="text-primary border-primary/40">
              Știri Auto
            </Badge>
            {hasGeo && (
              <Badge variant="outline" className="text-muted-foreground flex items-center gap-1">
                <MapPin size={10} />
                {geoInfo.cities?.[0] || geoInfo.counties?.[0]}
              </Badge>
            )}
          </div>
          <CardTitle className="text-xl md:text-2xl font-playfair group-hover:text-primary transition-colors leading-tight">
            <Link to={`${blogPrefix}/stiri/${post.slug}`}>
              {post.title}
            </Link>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground mb-4 line-clamp-3">
            {post.lead || post.meta_description || ""}
          </p>
          
          {displayTags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4">
              {displayTags.map((tag) => (
                <span
                  key={tag}
                  className={`text-xs px-2 py-0.5 rounded-full ${tagColors[tag] || "bg-muted text-muted-foreground"}`}
                >
                  #{tag.replace(/_/g, " ")}
                </span>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <Calendar size={14} />
                <span>{formatDate(post.published_at || post.created_at)}</span>
              </div>
              {post.read_time_minutes && (
                <div className="flex items-center gap-1">
                  <Clock size={14} />
                  <span>{post.read_time_minutes} min</span>
                </div>
              )}
            </div>
            <Link
              to={`${blogPrefix}/stiri/${post.slug}`}
              className="text-primary hover:underline flex items-center gap-1 font-medium"
            >
              Citește
              <ArrowRight size={14} />
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-all group">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <Badge variant="secondary" className="text-xs">
            {categoryLabel}
          </Badge>
          {hasGeo && (
            <Badge variant="outline" className="text-xs text-muted-foreground flex items-center gap-1">
              <MapPin size={10} />
              {geoInfo.cities?.[0] || geoInfo.counties?.[0]}
            </Badge>
          )}
        </div>
        <CardTitle className="text-lg font-playfair group-hover:text-primary transition-colors leading-tight line-clamp-2">
          <Link to={`${blogPrefix}/stiri/${post.slug}`}>
            {post.title}
          </Link>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-muted-foreground text-sm mb-3 line-clamp-2">
          {post.lead || post.meta_description || ""}
        </p>

        {displayTags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {displayTags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className={`text-xs px-1.5 py-0.5 rounded-sm ${tagColors[tag] || "bg-muted text-muted-foreground"}`}
              >
                #{tag.replace(/_/g, " ")}
              </span>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <Calendar size={12} />
              <span>{formatDate(post.published_at || post.created_at)}</span>
            </div>
            {post.read_time_minutes && (
              <div className="flex items-center gap-1">
                <Clock size={12} />
                <span>{post.read_time_minutes} min</span>
              </div>
            )}
          </div>
          <Link
            to={`${blogPrefix}/stiri/${post.slug}`}
            className="text-primary hover:underline flex items-center gap-1 font-medium"
          >
            Citește
            <ArrowRight size={12} />
          </Link>
        </div>
      </CardContent>
    </Card>
  );
};
